import { EVIDENCE_KINDS, validateProjectionEnvelope } from "./projectionContract";

export const WORKSPACE_PRODUCT_STATES = Object.freeze({
  LOGGED_OUT: "Logged Out",
  READY: "Ready",
  STARTING: "Starting",
  ACTIVE: "Active",
  PAUSED: "Paused",
  RECONNECTING: "Reconnecting",
  UNKNOWN: "Unknown",
  ENDING: "Ending",
  DISCONNECTED: "Disconnected",
});

export const WORKSPACE_EVENTS = Object.freeze({
  REBUILD: "rebuild",
  INVALIDATE: "invalidate",
  ACCOUNT_CHANGED: "account-changed",
  TEARDOWN: "teardown",
});

const TELEPHONY_STATE_MAP = Object.freeze({
  establishing: WORKSPACE_PRODUCT_STATES.STARTING,
  available: WORKSPACE_PRODUCT_STATES.ACTIVE,
  unavailable: WORKSPACE_PRODUCT_STATES.READY,
  paused: WORKSPACE_PRODUCT_STATES.PAUSED,
  ending: WORKSPACE_PRODUCT_STATES.ENDING,
  ended: WORKSPACE_PRODUCT_STATES.LOGGED_OUT,
});

export const INITIAL_WORKSPACE_STATE = Object.freeze({
  productState: WORKSPACE_PRODUCT_STATES.UNKNOWN,
  condition: "unknown",
  projections: Object.freeze({}),
  accountId: null,
  accountEpoch: null,
  diagnostic: "not-initialized",
});

function freezeState(state) {
  Object.freeze(state.projections);
  return Object.freeze(state);
}

export function composeWorkspaceState(projections, logicalNow) {
  const account = projections?.account;
  if (!account) return freezeState({ ...INITIAL_WORKSPACE_STATE, diagnostic: "account-missing" });

  const accountValidation = validateProjectionEnvelope(account, { logicalNow });
  if (!accountValidation.valid || account.data.status !== "resolved") {
    return freezeState({ ...INITIAL_WORKSPACE_STATE, diagnostic: "account-invalid" });
  }

  const accepted = {};
  for (const [name, projection] of Object.entries(projections)) {
    if (projection === undefined) continue;
    const result = validateProjectionEnvelope(projection, {
      expectedAccountId: account.accountId,
      logicalNow,
    });
    if (!result.valid) {
      return freezeState({
        ...INITIAL_WORKSPACE_STATE,
        diagnostic: result.errors.includes("account scope mismatch")
          ? "account-mismatch"
          : "projection-invalid",
      });
    }
    accepted[name] = projection;
  }

  const telephony = accepted.telephony;
  if (!telephony) {
    return freezeState({
      productState: WORKSPACE_PRODUCT_STATES.UNKNOWN,
      condition: "unknown",
      projections: accepted,
      accountId: account.accountId,
      accountEpoch: account.accountEpoch,
      diagnostic: "telephony-missing",
    });
  }

  if (
    [EVIDENCE_KINDS.UNKNOWN, EVIDENCE_KINDS.CONFLICTING, EVIDENCE_KINDS.RECONCILIATION_REQUIRED].includes(
      telephony.evidence.kind
    )
  ) {
    return freezeState({
      productState: WORKSPACE_PRODUCT_STATES.UNKNOWN,
      condition:
        telephony.evidence.kind === EVIDENCE_KINDS.RECONCILIATION_REQUIRED
          ? "reconciliation-required"
          : "unknown",
      projections: accepted,
      accountId: account.accountId,
      accountEpoch: account.accountEpoch,
      diagnostic: telephony.evidence.kind,
    });
  }

  const { sessionState, presenceState, connectionState } = telephony.data;
  let productState;
  if (connectionState === "disconnected") {
    productState = WORKSPACE_PRODUCT_STATES.DISCONNECTED;
  } else if (connectionState === "reconnecting") {
    productState = WORKSPACE_PRODUCT_STATES.RECONNECTING;
  } else if (sessionState === "ended") {
    productState = WORKSPACE_PRODUCT_STATES.LOGGED_OUT;
  } else if (presenceState === "paused") {
    productState = WORKSPACE_PRODUCT_STATES.PAUSED;
  } else {
    productState = TELEPHONY_STATE_MAP[sessionState] || TELEPHONY_STATE_MAP[presenceState];
  }

  return freezeState({
    productState: productState || WORKSPACE_PRODUCT_STATES.UNKNOWN,
    condition:
      telephony.evidence.kind === EVIDENCE_KINDS.FAILURE
        ? "failure"
        : telephony.evidence.kind === EVIDENCE_KINDS.PENDING ||
            productState === WORKSPACE_PRODUCT_STATES.STARTING ||
            productState === WORKSPACE_PRODUCT_STATES.ENDING
          ? "pending"
          : productState
            ? "stable"
            : "unknown",
    projections: accepted,
    accountId: account.accountId,
    accountEpoch: account.accountEpoch,
    diagnostic: productState ? null : "unmapped-owner-evidence",
  });
}

export function workspaceStateReducer(state = INITIAL_WORKSPACE_STATE, event) {
  switch (event?.type) {
    case WORKSPACE_EVENTS.REBUILD: {
      const hasOlderEvidence = Object.entries(event.projections || {}).some(
        ([name, projection]) =>
          projection &&
          state.projections[name] &&
          projection.accountId === state.projections[name].accountId &&
          projection.revision < state.projections[name].revision
      );
      if (hasOlderEvidence) return state;
      return composeWorkspaceState(event.projections, event.logicalNow);
    }
    case WORKSPACE_EVENTS.INVALIDATE:
    case WORKSPACE_EVENTS.ACCOUNT_CHANGED:
    case WORKSPACE_EVENTS.TEARDOWN:
      return freezeState({
        ...INITIAL_WORKSPACE_STATE,
        diagnostic: event.type,
      });
    default:
      return state;
  }
}

export function selectWorkspaceProductState(state) {
  return state.productState;
}

const SUPPORTED_PRODUCT_TRANSITIONS = Object.freeze({
  [WORKSPACE_PRODUCT_STATES.LOGGED_OUT]: Object.freeze([
    WORKSPACE_PRODUCT_STATES.LOGGED_OUT,
    WORKSPACE_PRODUCT_STATES.READY,
    WORKSPACE_PRODUCT_STATES.UNKNOWN,
  ]),
  [WORKSPACE_PRODUCT_STATES.READY]: Object.freeze([
    WORKSPACE_PRODUCT_STATES.READY,
    WORKSPACE_PRODUCT_STATES.STARTING,
    WORKSPACE_PRODUCT_STATES.ENDING,
    WORKSPACE_PRODUCT_STATES.UNKNOWN,
  ]),
  [WORKSPACE_PRODUCT_STATES.STARTING]: Object.freeze([
    WORKSPACE_PRODUCT_STATES.STARTING,
    WORKSPACE_PRODUCT_STATES.ACTIVE,
    WORKSPACE_PRODUCT_STATES.READY,
    WORKSPACE_PRODUCT_STATES.UNKNOWN,
  ]),
  [WORKSPACE_PRODUCT_STATES.ACTIVE]: Object.freeze([
    WORKSPACE_PRODUCT_STATES.ACTIVE,
    WORKSPACE_PRODUCT_STATES.PAUSED,
    WORKSPACE_PRODUCT_STATES.RECONNECTING,
    WORKSPACE_PRODUCT_STATES.ENDING,
    WORKSPACE_PRODUCT_STATES.UNKNOWN,
  ]),
  [WORKSPACE_PRODUCT_STATES.PAUSED]: Object.freeze([
    WORKSPACE_PRODUCT_STATES.PAUSED,
    WORKSPACE_PRODUCT_STATES.ACTIVE,
    WORKSPACE_PRODUCT_STATES.RECONNECTING,
    WORKSPACE_PRODUCT_STATES.ENDING,
    WORKSPACE_PRODUCT_STATES.UNKNOWN,
  ]),
  [WORKSPACE_PRODUCT_STATES.RECONNECTING]: Object.freeze([
    WORKSPACE_PRODUCT_STATES.RECONNECTING,
    WORKSPACE_PRODUCT_STATES.ACTIVE,
    WORKSPACE_PRODUCT_STATES.PAUSED,
    WORKSPACE_PRODUCT_STATES.DISCONNECTED,
    WORKSPACE_PRODUCT_STATES.UNKNOWN,
  ]),
  [WORKSPACE_PRODUCT_STATES.UNKNOWN]: Object.freeze([
    WORKSPACE_PRODUCT_STATES.UNKNOWN,
    WORKSPACE_PRODUCT_STATES.RECONNECTING,
  ]),
  [WORKSPACE_PRODUCT_STATES.ENDING]: Object.freeze([
    WORKSPACE_PRODUCT_STATES.ENDING,
    WORKSPACE_PRODUCT_STATES.LOGGED_OUT,
    WORKSPACE_PRODUCT_STATES.UNKNOWN,
  ]),
  [WORKSPACE_PRODUCT_STATES.DISCONNECTED]: Object.freeze([
    WORKSPACE_PRODUCT_STATES.DISCONNECTED,
    WORKSPACE_PRODUCT_STATES.LOGGED_OUT,
    WORKSPACE_PRODUCT_STATES.UNKNOWN,
  ]),
});

export function isSupportedProductStateTransition(from, to) {
  return SUPPORTED_PRODUCT_TRANSITIONS[from]?.includes(to) || false;
}
